import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ChannelPicker, CHANNEL_TYPE } from '@/components/ChannelPicker';
import { Switch } from '@/components/ui/switch';
import { DiscordMessagePreview } from '@/components/DiscordMessagePreview';
import { MediaPicker } from '@/components/MediaPicker';
import { useConfigPage, BackLink } from './_shared';

/**
 * Leave-Config — consumer is `Admin/events/Leave.js`, same shape as Welcome:
 *   leave.enabled, leave.channelId, leave.message, leave.backgroundUrl
 */
export function LeaveConfig() {
  const { guildId, settingsQuery, saveMut, currentConfig } = useConfigPage('leave');
  const [enabled, setEnabled] = useState(false);
  const [channelId, setChannelId] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [backgroundUrl, setBackgroundUrl] = useState('');

  useEffect(() => {
    if (settingsQuery.data) {
      setEnabled((currentConfig.enabled as boolean) ?? false);
      setChannelId((currentConfig.channelId as string) ?? null);
      setMessage((currentConfig.message as string) ?? '');
      setBackgroundUrl((currentConfig.backgroundUrl as string) ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsQuery.data]);

  if (settingsQuery.isLoading) return <p className="text-muted-foreground">Lade…</p>;

  return (
    <div className="mx-auto max-w-2xl">
      <BackLink guildId={guildId} />
      <h1 className="mb-6 text-2xl font-semibold">Leave — Konfiguration</h1>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Verabschiedungsnachricht</CardTitle>
          <CardDescription>
            Wo und wie Mitglieder verabschiedet werden. Platzhalter:{' '}
            <code>{'{USER}'}</code>, <code>{'{SERVER}'}</code>,{' '}
            <code>{'{MEMBERCOUNT}'}</code>.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Switch
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            label="Aktiviert"
          />

          <div>
            <Label>Leave-Channel</Label>
            <ChannelPicker
              guildId={guildId}
              value={channelId}
              onChange={setChannelId}
              types={[CHANNEL_TYPE.GUILD_TEXT]}
            />
          </div>
          <div>
            <Label>Nachricht</Label>
            <Textarea emoji
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={2000}
              placeholder="{USER} hat {SERVER} verlassen. Wir sind jetzt {MEMBERCOUNT} Mitglieder."
            />
            <div className="mt-1 text-right text-xs text-muted-foreground">
              {message.length}/2000
            </div>
          </div>
          <div>
            <Label>Hintergrund-Bild-URL (optional)</Label>
            <div className="flex gap-2">
              <Input
                value={backgroundUrl}
                onChange={(e) => setBackgroundUrl(e.target.value)}
                placeholder="https://example.com/leave-banner.png"
                type="url"
              />
              <MediaPicker
                guildId={guildId}
                currentUrl={backgroundUrl}
                onSelect={(url) => setBackgroundUrl(window.location.origin + url)}
              />
            </div>
          </div>

          <div className="flex items-center justify-end pt-2">
            <Button
              disabled={saveMut.isPending}
              onClick={() =>
                saveMut.mutate({
                  moduleConfig: {
                    enabled,
                    channelId,
                    message,
                    backgroundUrl: backgroundUrl || null,
                  },
                  scalars: { leaveChannelId: channelId },
                })
              }
            >
              {saveMut.isPending ? 'Speichere…' : 'Speichern'}
            </Button>
          </div>
          {saveMut.isSuccess && <p className="text-sm text-success">Gespeichert ✓</p>}
          {saveMut.error && (
            <p className="text-sm text-destructive">
              Fehler: {(saveMut.error as Error).message}
            </p>
          )}
        </CardContent>
      </Card>

      <div className="mt-6">
        <DiscordMessagePreview
          hint="Wie die Verabschiedung im gewählten Channel aussehen wird."
          embed={{
            title: '👋 Auf Wiedersehen!',
            description: message || '{USER} hat {SERVER} verlassen. Wir sind jetzt {MEMBERCOUNT} Mitglieder.',
            imageUrl: backgroundUrl || undefined,
            footer: { text: 'Member Count: {MEMBERCOUNT}' },
            timestamp: true,
            color: 0xed4245,
          }}
        />
      </div>
    </div>
  );
}
